import type { CashFlowTimelineBranch, CashFlowTimelineStep } from '@kioskgaming/ui';
import { formatInTimezone, getClientTimezone } from '@kioskgaming/ui';
import type { Transaction, TransactionAllocation } from '../types';
import type { AllocationFulfillmentSummary } from './transactionAllocationSummary';
import { transactionNeedsAllocationsFetch } from './transactionAllocationSummary';
import { resolveLifecycleFlowKind } from './transactionLifecycleFlow';

function normalizeAllocStatus(alloc: TransactionAllocation): string {
  return String(alloc.displayStatus || alloc.status || 'pending').toLowerCase();
}

function gameWalletLabel(alloc: TransactionAllocation): string {
  return alloc.gameProviderName || alloc.gameProviderCode || 'Game wallet';
}

function allocAmount(alloc: TransactionAllocation): number {
  return Math.abs(Number(alloc.amount || 0));
}

function formatCredits(amount: number, isOut: boolean): string {
  const formatted = Math.abs(amount).toLocaleString('en-US');
  return isOut ? `-${formatted} credits` : `+${formatted} credits`;
}

function formatCreditsBalance(amount: number | string | undefined | null): string | undefined {
  if (amount == null) return undefined;
  const n = Number(amount);
  if (!Number.isFinite(n)) return undefined;
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n)} credits`;
}

function formatTimestamp(iso?: string, timezone = getClientTimezone()): string | undefined {
  if (!iso) return undefined;
  const result = formatInTimezone(iso, timezone);
  return result === '—' ? undefined : result;
}

function isRefundedAllocation(alloc: TransactionAllocation): boolean {
  const status = normalizeAllocStatus(alloc);
  return status === 'refunded' || Boolean(alloc.refundTransactionId);
}

function isFailedAllocation(alloc: TransactionAllocation): boolean {
  const status = normalizeAllocStatus(alloc);
  return status === 'failed' || status === 'rejected' || isRefundedAllocation(alloc);
}

/** Multi-game deposit detail — synthesize master + game wallet money movement from allocations. */
export function shouldBuildAllocationCashFlow(
  tx: Transaction,
  allocations: TransactionAllocation[] | undefined,
): boolean {
  if (!allocations?.length) return false;
  if (tx.type !== 'deposit') return false;
  if (!transactionNeedsAllocationsFetch(tx)) return false;
  const kind = resolveLifecycleFlowKind(tx);
  return kind === 'deposit_wallet_to_game' || kind === 'deposit_gateway_to_game';
}

export function buildGameDepositAllocationCashFlowSteps(
  transaction: Transaction,
  allocations: TransactionAllocation[],
  summary: AllocationFulfillmentSummary,
  timezone = getClientTimezone(),
): CashFlowTimelineStep[] {
  const totalOut = allocations.reduce((sum, alloc) => sum + allocAmount(alloc), 0);
  const refundedAllocs = allocations.filter(isRefundedAllocation);
  const refundedTotal = refundedAllocs.reduce((sum, alloc) => sum + allocAmount(alloc), 0);
  const settledToGames = allocations
    .filter((alloc) => normalizeAllocStatus(alloc) === 'completed')
    .reduce((sum, alloc) => sum + allocAmount(alloc), 0);
  const netMasterOut = totalOut - refundedTotal;

  const debitLeg = transaction.relatedWalletTransactions?.find(
    (leg) => String(leg.type).toLowerCase() === 'debit',
  );

  const masterDebitStep: CashFlowTimelineStep = {
    id: 'master-debit',
    variant: 'source',
    walletName: 'Player master wallet',
    direction: 'out',
    amount: formatCredits(totalOut, true),
    status: 'success',
    actionLabel: debitLeg?.legSummary || debitLeg?.description || 'Debit master wallet for game deposit',
    timestamp: formatTimestamp(debitLeg?.created_at || transaction.created_at, timezone),
    hideStatusInDetails: true,
    balance:
      debitLeg?.balanceBefore != null && debitLeg?.balanceAfter != null
        ? {
            before: formatCreditsBalance(debitLeg.balanceBefore),
            after: formatCreditsBalance(debitLeg.balanceAfter),
          }
        : undefined,
    connectorAfter: { label: 'Allocate to game wallets' },
  };

  const branches: CashFlowTimelineBranch[] = allocations.map((alloc) => {
    const status = normalizeAllocStatus(alloc);
    const amount = allocAmount(alloc);
    const refunded = isRefundedAllocation(alloc);
    const completed = status === 'completed';

    let branchStatus: CashFlowTimelineBranch['status'] = 'pending';
    let actionLabel = 'Awaiting game sync';

    if (completed) {
      branchStatus = 'success';
      actionLabel = 'Credits synced to game';
    } else if (refunded || status === 'failed' || status === 'rejected') {
      branchStatus = 'failed';
      actionLabel = refunded ? 'Sync failed — refunded to master' : 'Game sync failed';
    }

    return {
      id: `alloc-${alloc.id}`,
      walletName: gameWalletLabel(alloc),
      subtitle: alloc.gameProviderCode ? String(alloc.gameProviderCode) : undefined,
      direction: 'in',
      amount: formatCredits(amount, false),
      badge: 'in',
      status: branchStatus,
      actionLabel,
      timestamp: formatTimestamp(alloc.updated_at || alloc.created_at, timezone),
    };
  });

  let balancedLabel: string | undefined;
  if (summary.fulfillmentStatus === 'completed') {
    balancedLabel = `${settledToGames.toLocaleString('en-US')} credits settled to games`;
  } else if (summary.fulfillmentStatus === 'partial') {
    balancedLabel = `${summary.completed}/${summary.total} games · ${refundedTotal.toLocaleString('en-US')} refunded · Net −${netMasterOut.toLocaleString('en-US')} master`;
  } else if (refundedTotal > 0 && settledToGames === 0) {
    balancedLabel = `${refundedTotal.toLocaleString('en-US')} credits refunded to master`;
  }

  const splitStatus =
    summary.fulfillmentStatus === 'completed'
      ? 'success'
      : summary.fulfillmentStatus === 'partial'
        ? 'pending'
        : summary.fulfillmentStatus === 'pending'
          ? 'pending'
          : 'failed';

  const splitStep: CashFlowTimelineStep = {
    id: 'game-allocation-split',
    variant: 'split',
    walletName: 'Player game wallets',
    direction: 'out',
    amount: formatCredits(totalOut, true),
    splitDescription: `Deposit split across ${allocations.length} game wallet${allocations.length === 1 ? '' : 's'}.`,
    balancedLabel,
    status: splitStatus,
    branches,
    connectorAfter: refundedTotal > 0 ? { label: 'Refund failed allocations to master' } : undefined,
  };

  const steps: CashFlowTimelineStep[] = [masterDebitStep, splitStep];

  for (const alloc of refundedAllocs) {
    const amount = allocAmount(alloc);
    steps.push({
      id: `refund-${alloc.id}`,
      walletName: 'Player master wallet',
      direction: 'in',
      amount: formatCredits(amount, false),
      status: 'success',
      actionLabel: `Refund · ${gameWalletLabel(alloc)} allocation failed`,
      timestamp: formatTimestamp(alloc.updated_at || alloc.created_at, timezone),
      hideStatusInDetails: true,
      metaKvs: alloc.refundTransactionId
        ? [{ label: 'Refund ref', value: alloc.refundTransactionId }]
        : undefined,
    });
  }

  if (steps.length > 0) {
    const last = steps[steps.length - 1];
    delete last.connectorAfter;
  }

  return steps;
}
